import type { Body, World } from "fast-2d";
import { MouseJoint, queryPoint } from "fast-2d";
import { collectDebugFrame, createDebugFrame, type DebugFrame } from "fast-2d/debug";
import type { Demo, Scene, SceneAction } from "../demos/types";
import { CanvasRenderer } from "../render/CanvasRenderer";
import { drawDebugFrame } from "../render/DebugOverlay";
import { Loop } from "../sim/Loop";

type PairCaptureWorld = World & { capturePairs: boolean };

export interface FrameStats {
	fps: number;
	bodies: number;
	awake: number;
	steps: number;
	simMs: number;
}

/** Owns the canvas, renderer, loop, and the currently-running demo scene. */
export class Gallery {
	private readonly renderer: CanvasRenderer;
	private readonly loop: Loop;
	private scene: Scene | null = null;
	private current: Demo | null = null;
	private paused = false;
	private fps = 60;
	private simMsEma = 0;
	private stepsWindow = 0;
	private windowElapsed = 0;
	private stepsPerSec = 0;
	private drag: MouseJoint | null = null;
	private debugEnabled = false;
	private readonly debugFrames = new Map<World, DebugFrame>();

	constructor(
		private readonly canvas: HTMLCanvasElement,
		private readonly onStats: (stats: FrameStats) => void,
	) {
		this.renderer = new CanvasRenderer(canvas);
		this.renderer.setDebugOverlay((world, ctx, camera) => {
			if (!this.debugEnabled) return;
			const frame = this.debugFrames.get(world);
			if (frame) drawDebugFrame(frame, ctx, camera);
		});
		this.loop = new Loop(this.frame);
		this.bindPointer();

		const ro = new ResizeObserver(() => this.resize());
		ro.observe(canvas);
		window.addEventListener("orientationchange", () => this.resize());
	}

	load(demo: Demo): void {
		this.current = demo;
		this.scene = demo.build();
		this.paused = false;
		this.drag = null;
		this.debugFrames.clear();
		this.simMsEma = 0;
		this.stepsWindow = 0;
		this.windowElapsed = 0;
		this.stepsPerSec = 0;
		this.resize();
		this.loop.start();
	}

	reset(): void {
		if (this.current) this.load(this.current);
	}

	/** Start/stop the loop based on viewport visibility to free the main thread. */
	setVisible(on: boolean): void {
		if (on) {
			if (this.scene) this.loop.start();
		} else {
			this.loop.stop();
		}
	}

	togglePause(): boolean {
		this.paused = !this.paused;
		return this.paused;
	}

	get isPaused(): boolean {
		return this.paused;
	}

	setDebugOverlay(enabled: boolean): void {
		this.debugEnabled = enabled;
		if (!enabled) {
			this.disablePairCapture();
			this.debugFrames.clear();
		}
	}

	get isDebugOverlayEnabled(): boolean {
		return this.debugEnabled;
	}

	/** Optional usage hint for the current scene (may contain HTML entities). */
	get hint(): string {
		return this.scene?.hint ?? "";
	}

	get actions(): readonly SceneAction[] {
		return this.scene?.actions?.() ?? [];
	}

	private resize(): void {
		if (this.scene) this.renderer.resize(this.scene.camera);
	}

	private readonly frame = (dt: number): void => {
		const scene = this.scene;
		if (!scene) return;

		const t0 = performance.now();
		if (!this.paused) {
			scene.onBeforeStep?.(dt);
		}
		const steps = this.paused ? 0 : scene.update ? scene.update(dt) : scene.world.update(dt);
		if (this.debugEnabled) this.collectDebugFrames(scene);
		const simMs = performance.now() - t0;

		if (scene.render) scene.render(this.renderer);
		else this.renderer.render(scene.world, scene.camera, scene.draw, scene.hiddenBodies);

		// Smooth the readouts so they don't flicker. The fixed-step accumulator
		// runs 0 or 1 steps on any given frame at high refresh rates, so we
		// report steps/sec over a rolling window and an EMA of per-step cost.
		this.fps += ((dt > 0 ? 1 / dt : 60) - this.fps) * 0.1;
		if (steps > 0) {
			this.simMsEma += (simMs / steps - this.simMsEma) * 0.2;
		}
		this.stepsWindow += steps;
		this.windowElapsed += dt;
		if (this.windowElapsed >= 0.5) {
			this.stepsPerSec = Math.round(this.stepsWindow / this.windowElapsed);
			this.stepsWindow = 0;
			this.windowElapsed = 0;
		}
		const statWorlds = scene.worlds ?? [scene.world];
		this.onStats({
			fps: Math.round(this.fps),
			bodies: countBodies(statWorlds),
			awake: countAwake(statWorlds),
			steps: this.stepsPerSec,
			simMs: this.simMsEma,
		});
	};

	private collectDebugFrames(scene: Scene): void {
		const worlds = scene.worlds ?? [scene.world];
		for (let i = 0; i < worlds.length; i++) {
			const world = worlds[i]!;
			let frame = this.debugFrames.get(world);
			if (!frame) {
				frame = createDebugFrame(debugCapacityFor(world));
				this.debugFrames.set(world, frame);
			}
			collectDebugFrame(world, frame, { includePairs: true });
		}
	}

	private disablePairCapture(): void {
		const scene = this.scene;
		if (!scene) return;
		const worlds = scene.worlds ?? [scene.world];
		for (let i = 0; i < worlds.length; i++) (worlds[i]! as PairCaptureWorld).capturePairs = false;
	}

	private bindPointer(): void {
		const toWorld = (e: PointerEvent): [number, number] => {
			const rect = this.canvas.getBoundingClientRect();
			const cam = this.scene!.camera;
			return [
				cam.screenToWorldX(e.clientX - rect.left),
				cam.screenToWorldY(e.clientY - rect.top),
			];
		};

		this.canvas.addEventListener("pointerdown", (e) => {
			const scene = this.scene;
			if (!scene) return;
			this.canvas.setPointerCapture(e.pointerId);
			const [x, y] = toWorld(e);

			// Grab a dynamic body under the cursor and drag it (unless opted out).
			if (scene.draggable !== false) {
				const pickRadius = Math.max(0.12, 10 / scene.camera.scale);
				const grabbed = grabbableAt(scene.world, x, y, pickRadius);
				if (grabbed) {
					this.drag = scene.world.addConstraint(
						new MouseJoint(grabbed, {
							target: [x, y],
							localAnchor: grabbed.toLocal([x, y]),
							maxForce: Math.max(1200 * grabbed.mass, 80),
							frequency: 6,
							dampingRatio: 0.8,
						}),
					);
					return;
				}
			}
			// Empty space → let the demo handle the press (drop weights, fling, etc.).
			scene.onPointerDown?.(x, y);
		});
		this.canvas.addEventListener("pointermove", (e) => {
			const scene = this.scene;
			if (!scene) return;
			const [x, y] = toWorld(e);
			if (this.drag) {
				this.drag.target.set(x, y);
				return;
			}
			scene.onPointerMove?.(x, y);
		});
		const endDrag = (e: PointerEvent): void => {
			const scene = this.scene;
			if (!scene) return;
			const [x, y] = toWorld(e);
			if (this.drag) {
				scene.world.removeConstraint(this.drag);
				this.drag = null;
				return;
			}
			scene.onPointerUp?.(x, y);
		};
		this.canvas.addEventListener("pointerup", endDrag);
		this.canvas.addEventListener("pointercancel", endDrag);
	}
}

function debugCapacityFor(world: World) {
	const bodies = Math.max(64, world.bodies.length + 96);
	let circles = 0;
	let polygons = 0;
	let polygonVertices = 0;
	for (let i = 0; i < world.bodies.length; i++) {
		const body = world.bodies[i]!;
		if (body.isCompound || body.partCount > 1) {
			for (let j = 0; j < body.parts.length; j++) {
				const part = body.parts[j]!;
				if (part.isCircle) circles++;
				else {
					polygons++;
					polygonVertices += part.vertexCount;
				}
			}
		} else if (body.isCircle) circles++;
		else {
			polygons++;
			polygonVertices += body.vertexCount;
		}
	}
	return {
		bodies,
		circles: Math.max(bodies, circles + 32),
		polygons: Math.max(bodies, polygons + 32),
		polygonVertices: Math.max(bodies * 6, polygonVertices + 128),
		aabbs: bodies,
		contacts: bodies * 4,
		contactPoints: bodies * 8,
		pairs: bodies * 8,
		jointSegments: Math.max(128, world.constraints.length * 3 + 32),
		islands: bodies,
	};
}

function countBodies(worlds: readonly World[]): number {
	let n = 0;
	for (let i = 0; i < worlds.length; i++) n += worlds[i]!.bodies.length;
	return n;
}

function countAwake(worlds: readonly World[]): number {
	let n = 0;
	for (let w = 0; w < worlds.length; w++) {
		const bodies = worlds[w]!.bodies;
		for (let i = 0; i < bodies.length; i++) {
			const b = bodies[i]!;
			if (!b.isStatic && !b.isSleeping) n++;
		}
	}
	return n;
}

/** The topmost dynamic body under or near a world point, or `null` if none. */
function grabbableAt(world: World, x: number, y: number, radius: number): Body | null {
	const hits = queryPoint(world.bodies, [x, y]);
	// Prefer the last (most recently added / visually topmost) dynamic body.
	for (let i = hits.length - 1; i >= 0; i--) {
		const b = hits[i]!;
		if (!b.isStatic) return b;
	}

	let best: Body | null = null;
	let bestDistanceSq = radius * radius;
	const bodies = world.bodies;
	for (let i = bodies.length - 1; i >= 0; i--) {
		const body = bodies[i]!;
		if (body.isStatic) continue;
		body.synchronize();
		if (x < body.minX - radius || x > body.maxX + radius) continue;
		if (y < body.minY - radius || y > body.maxY + radius) continue;
		const distanceSq = distanceSqToBody(body, x, y);
		if (distanceSq <= bestDistanceSq) {
			best = body;
			bestDistanceSq = distanceSq;
		}
	}
	return best;
}

function distanceSqToBody(body: Body, x: number, y: number): number {
	if (body.isCircle) {
		const dx = x - body.position.x;
		const dy = y - body.position.y;
		const distance = Math.max(0, Math.hypot(dx, dy) - body.radius);
		return distance * distance;
	}

	const verts = body.worldVerts;
	const normals = body.worldNormals;
	let inside = true;
	let bestEdgeDistanceSq = Infinity;
	for (let i = 0; i < body.vertexCount; i++) {
		const j = i + 1 < body.vertexCount ? i + 1 : 0;
		const ax = verts[i * 2]!;
		const ay = verts[i * 2 + 1]!;
		const bx = verts[j * 2]!;
		const by = verts[j * 2 + 1]!;
		if (normals[i * 2]! * (x - ax) + normals[i * 2 + 1]! * (y - ay) > 0) inside = false;
		const edgeDistanceSq = distanceSqToSegment(x, y, ax, ay, bx, by);
		if (edgeDistanceSq < bestEdgeDistanceSq) bestEdgeDistanceSq = edgeDistanceSq;
	}
	return inside ? 0 : bestEdgeDistanceSq;
}

function distanceSqToSegment(
	x: number,
	y: number,
	ax: number,
	ay: number,
	bx: number,
	by: number,
): number {
	const abx = bx - ax;
	const aby = by - ay;
	const denom = abx * abx + aby * aby;
	const t = denom > 0 ? Math.max(0, Math.min(1, ((x - ax) * abx + (y - ay) * aby) / denom)) : 0;
	const dx = x - (ax + abx * t);
	const dy = y - (ay + aby * t);
	return dx * dx + dy * dy;
}
